"use client";

import { useT } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { usePathname } from "next/navigation";

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

declare global {
  interface Window {
    __tcfapi?: (command: string, version: number, callback: () => void) => void;
    __uspapi?: (command: string) => void;
  }
}

function FeedbackModal({
  onClose,
  page,
}: {
  onClose: () => void;
  page: string;
}) {
  const t = useT();
  const [type, setType] = useState("Bug");
  const [contact, setContact] = useState("");
  const [contents, setContents] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit() {
    if (!contents.trim() || !contact.trim()) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          contact: contact.trim(),
          contents: `[Page: ${page}]\n\n${contents.trim()}`,
        }),
      });
      if (!res.ok) throw new Error("Failed to send");
      setSent(true);
      setTimeout(onClose, 1500);
    } catch {
      setError(t("Failed to send. Please try again."));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-scrim/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] shadow-2xl shadow-scrim/50 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-[var(--text-primary)] mb-4">
          {t("Submit Feedback")}
        </h2>

        {sent ? (
          <p className="text-success text-sm py-4">
            {t("Sent successfully. Thank you!")}
          </p>
        ) : (
          <>
            <label className="block text-sm text-[var(--text-secondary)] mb-1">
              {t("Page")}
            </label>
            <input
              type="text"
              value={page}
              readOnly
              className="w-full mb-4 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-muted)] text-sm cursor-default"
            />

            <label className="block text-sm text-[var(--text-secondary)] mb-1">
              {t("Type")}
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full mb-4 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent-gold)]"
            >
              <option value="Bug">{t("Bug")}</option>
              <option value="Feature Request">{t("Feature Request")}</option>
              <option value="Localization">{t("Localization")}</option>
            </select>

            <label className="block text-sm text-[var(--text-secondary)] mb-1">
              {t("Discord Username or Email")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={t("username#1234 or email@example.com")}
              className="w-full mb-4 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent-gold)]"
            />

            <label className="block text-sm text-[var(--text-secondary)] mb-1">
              {t("Contents")} <span className="text-danger">*</span>
            </label>
            <textarea
              value={contents}
              onChange={(e) => setContents(e.target.value)}
              rows={5}
              placeholder={t("Describe the bug or feature request...")}
              className="w-full mb-4 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent-gold)] resize-none"
            />

            {error && <p className="text-danger text-sm mb-3">{error}</p>}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={sending || !contents.trim() || !contact.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--accent-gold)] text-[var(--bg-primary)] hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {sending ? t("Sending...") : t("Submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Footer() {
  const t = useT();
  const pathname = usePathname();
  const [showFeedback, setShowFeedback] = useState(false);

  // Open the feedback modal when navigated to with `#feedback` in the URL
  // (the Contact menu in the navbar uses this anchor). Listening to
  // `hashchange` on top of the initial check covers both first-load and
  // intra-page nav. Closing the modal also strips the hash so the URL
  // doesn't keep re-opening it on back/forward.
  useEffect(() => {
    function checkHash() {
      if (window.location.hash === "#feedback") setShowFeedback(true);
    }
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  function closeFeedback() {
    setShowFeedback(false);
    if (window.location.hash === "#feedback") {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }

  return (
    <footer className="border-t border-[var(--border-subtle)] mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[var(--text-muted)]">
        <a
          href={`${API_BASE}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("API")}
        </a>
        <Link
          prefetch={false}
          href="/developers"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Developers")}
        </Link>
        <a
          href="https://github.com/ptrlrd/spire-codex"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          GitHub
        </a>
        <a
          href="https://discord.gg/xMsTBeh"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          Discord
        </a>
        <a
          href="https://ko-fi.com/spirecodex"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          Ko-Fi
        </a>
        <button
          onClick={() => setShowFeedback(true)}
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Submit Feedback")}
        </button>
        <Link
          prefetch={false}
          href="/privacy"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Privacy")}
        </Link>
        <button
          onClick={() => window.__tcfapi?.("displayConsentUi", 2, () => {})}
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Manage Consent")}
        </button>
        <button
          onClick={() => window.__uspapi?.("displayUspUi")}
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Do Not Sell My Personal Information")}
        </button>
        <Link
          prefetch={false}
          href="/terms"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Terms")}
        </Link>
        <Link
          prefetch={false}
          href="/beta"
          className="hover:text-[var(--accent-gold)] transition-colors"
        >
          {t("Beta Site")}
        </Link>
      </div>
      {showFeedback && (
        <FeedbackModal onClose={closeFeedback} page={pathname} />
      )}
    </footer>
  );
}
